import { CurrencyEnum } from '@/enums';
import { amountToString } from '@/functions';
import { cn } from '@/lib/utils';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Calendar
} from '@/components/ui';
import { Calendar as CalendarIcon, TrendingUp, TrendingDown, ChevronLeft, ChevronRight, Landmark, Plus } from 'lucide-react';
import { format, addMonths, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTransactions } from '../hooks';
import TransactionFilters from './TransactionFilters';
import { useQueryParams } from '@/hooks';
import { useRequestGetAccountingSummary } from '@/hooks/httpRequest';

import HeaderPageMobile from './HeaderPageMobile';

const getMonthlyPeriod = (ref: Date): { from: Date; to: Date } => ({
    from: startOfMonth(ref),
    to: endOfMonth(ref)
});

const HeaderPage = () => {
    const { isLoading: isLoadingTransactions, createTransaction } = useTransactions();
    const { values: [periodFrom, periodTo], setValues } = useQueryParams(['periodFrom', 'periodTo']);

    const dateRange = (() => {
        if (periodFrom && periodTo) {
            return { from: new Date(periodFrom), to: new Date(periodTo) };
        }
        return getMonthlyPeriod(new Date());
    })();

    const { summary, isLoading: isLoadingSummary } = useRequestGetAccountingSummary({
        periodFrom: dateRange.from.toISOString(),
        periodTo: dateRange.to.toISOString()
    });

    const isLoading = isLoadingTransactions || isLoadingSummary;
    const treasury = summary?.treasury ?? 0;
    const periodIncome = summary?.periodIncome ?? 0;
    const periodExpenses = summary?.periodExpenses ?? 0;

    const handleDateRangeChange = (range: { from?: Date; to?: Date }) => {
        if (range.from && range.to) {
            setValues({
                periodFrom: startOfMonth(range.from).toISOString(),
                periodTo: endOfMonth(range.to).toISOString()
            });
        }
    };

    const handleQuickPeriod = (period: 'current' | 'previous' | 'next') => {
        const ref = dateRange.from;
        let base: Date;
        switch (period) {
            case 'previous': base = subMonths(ref, 1); break;
            case 'next': base = addMonths(ref, 1); break;
            default: base = new Date();
        }
        const { from, to } = getMonthlyPeriod(base);
        setValues({
            periodFrom: from.toISOString(),
            periodTo: to.toISOString()
        });
    };

    return (
        <div className="flex flex-col space-y-4">
            <HeaderPageMobile
                incomeTotal={periodIncome}
                expenseTotal={periodExpenses}
                netBalance={periodIncome - periodExpenses}
                dateRange={dateRange}
                onDateRangeChange={handleDateRangeChange}
                onQuickPeriod={handleQuickPeriod}
                isLoading={isLoading}
                onOpenCreate={() => createTransaction()}
            />

            <div className="hidden md:flex flex-col space-y-4">
                <div className='flex justify-end'>
                    <Button
                        onClick={() => createTransaction()}
                        className='w-auto h-9 bg-black text-white hover:bg-black/90 space-x-2 font-semibold shadow-xs'>
                        <Plus className="h-4 w-4 mr-1" />
                        <span>Créer une transaction</span>
                    </Button>
                </div>

                <div className="flex flex-row items-center justify-between gap-4">
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight shrink-0">Transactions</h2>
                    </div>

                    <div className='flex flex-row items-center gap-4'>
                        <TransactionFilters isLoading={isLoading} />

                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-9 px-2"
                                onClick={() => handleQuickPeriod('previous')}
                                disabled={isLoading}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className={cn('h-9 w-[220px] justify-start text-left font-normal text-sm')}
                                        disabled={isLoading}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                                        <span className="truncate">
                                            {format(dateRange.from, 'dd MMM', { locale: fr })} - {format(dateRange.to, 'dd MMM yyyy', { locale: fr })}
                                        </span>
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="end">
                                    <Calendar
                                        mode="range"
                                        defaultMonth={dateRange.from}
                                        selected={{ from: dateRange.from, to: dateRange.to }}
                                        onSelect={(range) => range && handleDateRangeChange(range)}
                                        numberOfMonths={2}
                                    />
                                    <div className="flex gap-2 p-3 border-t">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex-1"
                                            onClick={() => handleQuickPeriod('current')}
                                        >
                                            Ce mois
                                        </Button>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="flex-1"
                                            onClick={() => handleQuickPeriod('previous')}
                                        >
                                            Mois préc.
                                        </Button>
                                    </div>
                                </PopoverContent>
                            </Popover>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-9 px-2"
                                onClick={() => handleQuickPeriod('next')}
                                disabled={isLoading}
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                    {/* Trésorerie — toutes périodes, couleur orange */}
                    <Card className="border-orange-500/20 bg-orange-500/5">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                Trésorerie
                            </CardTitle>
                            <Landmark className="h-4 w-4 text-orange-500" />
                        </CardHeader>
                        <CardContent>
                            <div className={cn(
                                'text-2xl font-bold',
                                treasury >= 0 ? 'text-orange-500' : 'text-red-500'
                            )}>
                                {amountToString(treasury, CurrencyEnum.EUR)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">Toutes périodes confondues</p>
                        </CardContent>
                    </Card>

                    {/* Total Revenus — période sélectionnée */}
                    <Card className="border-emerald-500/20 bg-emerald-500/5">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                Total Revenus
                            </CardTitle>
                            <TrendingUp className="h-4 w-4 text-emerald-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-emerald-500">
                                {amountToString(periodIncome, CurrencyEnum.EUR)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {format(dateRange.from, 'dd MMM', { locale: fr })} – {format(dateRange.to, 'dd MMM', { locale: fr })}
                            </p>
                        </CardContent>
                    </Card>

                    {/* Dépenses — période sélectionnée */}
                    <Card className="border-red-500/20 bg-red-500/5">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium text-muted-foreground">
                                Dépenses
                            </CardTitle>
                            <TrendingDown className="h-4 w-4 text-red-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold text-red-500">
                                {amountToString(periodExpenses > 0 ? -periodExpenses : periodExpenses, CurrencyEnum.EUR)}
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {format(dateRange.from, 'dd MMM', { locale: fr })} – {format(dateRange.to, 'dd MMM', { locale: fr })}
                            </p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default HeaderPage;
